// Console error ring buffer.
//
// Captures the last N console.error / uncaught-error / unhandledrejection
// entries so the SOS modal can attach them to the incident report. Agents
// mid-shift shouldn't have to remember what the console said — we
// snapshot it automatically.
//
// Design goals:
//   * Zero perf impact when idle — install once, tiny ring buffer.
//   * Safe to be called before install — read() returns [] until installed.
//   * Never throw — a broken error handler is the last thing we need.
//   * Redact tokens — basic Bearer/API-key/JWT scrubbing so we don't leak
//     agent creds into an incident payload.

const MAX_ENTRIES = 20;
const MAX_LEN = 500;

const _ring = [];
let _installed = false;

function scrub(s) {
  try {
    let out = String(s);
    // JWTs (three base64url chunks separated by dots)
    out = out.replace(/eyJ[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}/g, '[REDACTED_JWT]');
    // Bearer tokens
    out = out.replace(/Bearer\s+[A-Za-z0-9._-]{10,}/gi, 'Bearer [REDACTED]');
    // api_key= / apikey= / token= / password= query/param values
    out = out.replace(/(api[_-]?key|apikey|token|password)=([^\s&"]{6,})/gi, '$1=[REDACTED]');
    if (out.length > MAX_LEN) out = out.slice(0, MAX_LEN);
    return out;
  } catch {
    return '[unreadable]';
  }
}

function push(kind, parts) {
  try {
    const line = `[${kind}] ${parts.map((p) => (p instanceof Error ? `${p.name}: ${p.message}` : String(p))).join(' ')}`;
    _ring.push(scrub(line));
    while (_ring.length > MAX_ENTRIES) _ring.shift();
  } catch { /* swallow */ }
}

export function installConsoleErrorRing() {
  if (_installed || typeof window === 'undefined') return;
  _installed = true;
  try {
    const origError = console.error?.bind(console);
    if (origError) {
      // eslint-disable-next-line no-console
      console.error = (...args) => { push('console.error', args); origError(...args); };
    }
    window.addEventListener('error', (ev) => {
      push('window.error', [ev.error || ev.message || '(unknown)', ev.filename ? `at ${ev.filename}:${ev.lineno}` : '']);
    });
    window.addEventListener('unhandledrejection', (ev) => {
      push('unhandledrejection', [ev.reason || '(unknown)']);
    });
  } catch { /* if we can't hook, we simply return [] on read */ }
}

/** Snapshot current buffer. Never mutates. */
export function readConsoleErrorRing() {
  return _ring.slice(-MAX_ENTRIES);
}

/** Test helper only. */
export function __resetConsoleErrorRing() { _ring.length = 0; _installed = false; }
